import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  CircleDashed,
  Loader2,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import { listDemoEnvironmentRuns } from '../../services/api';
import type {
  DemoAgencySetupStepStatus,
  DemoEnvironmentResponse,
  DemoEnvironmentRunResponse,
} from '../../services/api/types.gen';
import { formatDateInPT } from '../../utils/dateUtils';
import {
  STEP_LABELS,
  displaySteps,
  messageParts,
  runLabel,
} from './demoRunSteps';

/**
 * One agency's runs, shown when its row is expanded.
 *
 * The latest run comes from the list, which polls while it is running, so its
 * checklist fills in live. Earlier runs are fetched once when the row opens and
 * again when a run finishes, so a new run does not hide the last one's steps.
 */

interface Props {
  environment: DemoEnvironmentResponse;
}

export const DemoEnvironmentRunDetails: React.FC<Props> = ({ environment }) => {
  const latest = environment.latest_run ?? null;
  const [history, setHistory] = useState<DemoEnvironmentRunResponse[]>([]);
  const [historyFailed, setHistoryFailed] = useState(false);

  // Refetch when the latest run changes or finishes; not on every poll.
  const latestKey = latest ? `${latest.id}:${latest.status}` : '';
  useEffect(() => {
    let cancelled = false;
    listDemoEnvironmentRuns({
      path: { agency_id: environment.agency_id },
      throwOnError: true,
    })
      .then(res => {
        if (cancelled) return;
        setHistory(res.data?.runs ?? []);
        setHistoryFailed(false);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load demo sandbox runs', error);
        setHistoryFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [environment.agency_id, latestKey]);

  if (!latest) {
    return (
      <p className='text-sm text-muted-foreground'>
        No runs yet. Run it to set this sandbox up.
      </p>
    );
  }

  const earlier = history.filter(run => run.id !== latest.id);

  return (
    <div className='space-y-2'>
      <RunChecklist run={latest} />
      {historyFailed && (
        <p className='text-sm text-red-700'>
          Unable to load earlier runs. Please try again.
        </p>
      )}
      {earlier.length > 0 && (
        <div className='space-y-1'>
          <div className='text-xs font-medium text-muted-foreground'>
            Earlier runs
          </div>
          {earlier.map(run => (
            <details key={run.id} className='text-xs'>
              <summary className='cursor-pointer'>
                <RunHeadline run={run} />
              </summary>
              <div className='pb-1 pl-4 pt-1'>
                <RunChecklist run={run} />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

const RunHeadline: React.FC<{ run: DemoEnvironmentRunResponse }> = ({
  run,
}) => (
  <span className={run.status === 'failed' ? 'text-red-700' : undefined}>
    {runLabel(run)} {formatDateInPT(run.finished_at ?? run.started_at)}
    {run.summary && (
      <span className='text-muted-foreground'> — {run.summary}</span>
    )}
  </span>
);

const STEP_ICONS: Record<
  DemoAgencySetupStepStatus,
  { Icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  succeeded: { Icon: CheckCircle2, className: 'text-green-600' },
  failed: { Icon: XCircle, className: 'text-red-600' },
  skipped: { Icon: MinusCircle, className: 'text-muted-foreground' },
  pending: { Icon: Circle, className: 'text-muted-foreground/50' },
  not_implemented: { Icon: CircleDashed, className: 'text-muted-foreground' },
};

/**
 * One row per step: status icon and label on the left, the message on the right.
 * Messages pack several results into one string, so each part gets its own line.
 */
const RunChecklist: React.FC<{ run: DemoEnvironmentRunResponse }> = ({
  run,
}) => {
  const steps = displaySteps(run);
  // The first pending step of a running run is the one in progress.
  const currentId =
    run.status === 'running'
      ? steps.find(step => step.status === 'pending')?.id
      : undefined;
  return (
    <ul
      aria-label={`Steps for the run started ${formatDateInPT(run.started_at)}`}
      className='space-y-2 text-xs'
    >
      {steps.map(step => {
        const isCurrent = step.id === currentId;
        const { Icon, className } = STEP_ICONS[step.status];
        return (
          <li
            key={step.id}
            className='grid grid-cols-[1rem_11rem_minmax(0,1fr)] items-start gap-x-2'
          >
            {isCurrent ? (
              <Loader2 className='mt-px h-3.5 w-3.5 animate-spin text-blue-600' />
            ) : (
              <Icon className={`mt-px h-3.5 w-3.5 ${className}`} />
            )}
            <span className='sr-only'>
              {isCurrent ? 'running' : step.status}
            </span>
            <span
              className={`font-medium ${step.status === 'pending' && !isCurrent ? 'text-muted-foreground' : ''}`}
            >
              {STEP_LABELS[step.id] ?? step.id}
            </span>
            <div className='min-w-0 space-y-0.5 text-muted-foreground'>
              {messageParts(step.message).map((part, index) => (
                <div key={index} className='break-words'>
                  {part.label && (
                    <span className='text-foreground'>{part.label}: </span>
                  )}
                  {part.text}
                </div>
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default DemoEnvironmentRunDetails;
